import React, { useEffect, useState } from 'react';
import Multiselect from 'multiselect-react-dropdown';
import { uploadImage } from '../../services/api/implementation/impl';
import { multipleUploadFiles, uploadFile } from '../../../builder/features/files';
import calendar from '../../../shared/assets/svg/calendar.svg';
import { useDispatch } from 'react-redux';
import toast from 'react-hot-toast';
import { RiEyeCloseLine, RiEyeFill } from 'react-icons/ri';
import Button from './Button';
import { FiUpload } from 'react-icons/fi';
import { useRef } from 'react';

const MAX_FILE_SIZE = 20 * 1024 * 1024;

const FormInput = ({
  type,
  options,
  register,
  label,
  width,
  isRequired,
  register_key,
  defaultValuePre,
  valuePre,
  onType,
  maxLength,
  CustomStyle,
  CustomFeature,
  errors,
  minLength,
  placeholder,
  defaultbtn,
  onClick,
  accept,
  uploadFunction,
  uploadFunctionId,
  onDropdownChange,
  onSearchChange,
  maxFileSize,
  prefilledFiles = [],
  autoComplete,
  // -----------------------
  // ADDED readOnly prop:
  readOnly = false,
  allvalues,
  hideAstrik = false
  // -----------------------
}) => {
  const style = { width: `${width}%` };
  const dispatch = useDispatch();

  const [selectedValues, setSelectedValues] = useState(valuePre || []);
  const [file, setFile] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState(prefilledFiles || []);
  const [isEyeClick, setIsEyeClick] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (prefilledFiles.length > 0) {
      setUploadedFiles(prefilledFiles);
    }
  }, [prefilledFiles]);

  useEffect(() => {
    if (type === 'file-secondary') {
      console.log('Current uploadedFiles:', uploadedFiles);
    }
  }, [uploadedFiles, type]);

  // Handle clicking outside dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const actualMaxFileSize = maxFileSize || MAX_FILE_SIZE;

  const validateFileSize = (file) => {
    if (!file || !file.size) {
      toast.error('Invalid file. Please try uploading again.');
      return false;
    }
    if (file.size > actualMaxFileSize) {
      const sizeInMB = actualMaxFileSize / (1024 * 1024);
      toast.error(`File "${file.name}" is too large. Maximum size is ${sizeInMB}MB.`);
      return false;
    }
    return true;
  };

  const handleFileChange = async (e) => {
    // ---------- Only change: if readOnly => skip ----------
    if (readOnly) return;
    // ------------------------------------------------------
    if (type === 'file-secondary') {
      const newFiles = Array.from(e.target.files).filter(validateFileSize);
      const uniqueNewFiles = newFiles.filter(
        (newFile) => !uploadedFiles.some((existingFile) => existingFile.name === newFile.name)
      );

      if (uniqueNewFiles.length < newFiles.length) {
        toast.warning('Some files were skipped (duplicates or size limits).');
      }
      await uploadMultipleFiles(uniqueNewFiles);
    } else if (type === 'file-btn' || type === 'file-icon-btn') {
      const selectedFile = e.target.files[0];
      if (selectedFile && validateFileSize(selectedFile)) {
        await uploadSingleFile(selectedFile);
      }
    }
  };

  const uploadMultipleFiles = async (files) => {
    if (files.length === 0) return;
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });

    try {
      console.log('Attempting to upload files...');
      const result = await uploadImage(formData);
      const urls = result.data.files?.map((item) => ({
        name: item.originalName,
        url: item.presignedUrl
      }));

      setUploadedFiles((prevFiles) => [...prevFiles, ...urls]);

      dispatch(
        multipleUploadFiles({
          fileType: register_key,
          urls: [...uploadedFiles, ...urls]
        })
      );

      toast.success(`${files.length} file(s) uploaded successfully!`);
    } catch (error) {
      console.error('Error uploading files:', error);
      toast.error('Error uploading files. Please try again.');
    }
  };

  const uploadSingleFile = async (file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append('files', file);

    try {
      if (register_key === 'allotment_signed_doc' || register_key === 'agreement_signed_doc') {
        await uploadFunction(uploadFunctionId, formData);
        toast.success('File uploaded successfully!');
      } else {
        const result = await uploadImage(formData);
        const url = result.data.files?.map((item) => ({
          name: item.originalName,
          url: item.presignedUrl
        }));
        dispatch(
          uploadFile({
            fileType: register_key,
            url
          })
        );
        if (register_key === 'company_logo') {
          toast.success('Logo uploaded successfully!');
        } else {
          toast.success('File uploaded successfully!');
        }
        setUploadedFiles(url);
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Error uploading file. Please try again.');
    }
  };

  const handleRemoveFile = (index) => {
    if (type === 'file-secondary') {
      const updatedFiles = uploadedFiles.filter((_, i) => i !== index);
      setUploadedFiles(updatedFiles);
      dispatch(
        multipleUploadFiles({
          fileType: register_key,
          urls: updatedFiles
        })
      );
    } else if (type === 'file-btn' || type === 'file-icon-btn') {
      setUploadedFiles([]);
      setFile(null);
      dispatch(
        uploadFile({
          fileType: register_key,
          url: []
        })
      );
    }
  };

  const renderSelectedFiles = () => {
    if (uploadedFiles.length > 0) {
      return (
        <div className="flex flex-col gap-2 mt-2">
          <p className="text-gray-600 text-sm">Uploaded Files:</p>
          {uploadedFiles.map((f, index) => (
            <div key={index} className="flex items-center bg-gray-100 p-1 rounded text-sm">
              <span className="truncate max-w-[200px]">{f.name}</span>
              {
                // ---------- Only remove button if not readOnly ----------
                !readOnly && (
                  <button
                    type="button"
                    onClick={() => handleRemoveFile(index)}
                    className="ml-2 text-red-500 hover:text-red-700">
                    ×
                  </button>
                )
              }
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  const handleChangedropdown = (e) => {
    console.log('handleChangedropdown called with:', e);
    console.log('e.target.value:', e.target.value);

    if (e.target.value === 'action-option') {
      onClick && onClick();
    } else {
      console.log('Selected:', e.target.value);
      onDropdownChange && onDropdownChange(e.target.value);
    }

    // Find the full object from options
    const selectedOption = options?.find((opt) => String(opt.value) === String(e.target.value));
    console.log('selectedOption found:', selectedOption);

    // Call the custom handler with the full object
    if (allvalues) {
      allvalues(selectedOption);
    }

    // Update the form value using setValue if available
    if (register && typeof register === 'function') {
      try {
        console.log('Calling register onChange');
        register(`${register_key}`).onChange(e);
      } catch (error) {
        console.log('Error calling register onChange:', error);
        // If register doesn't have onChange, just call register
        register(`${register_key}`);
      }
    }
  };

  // Add keyboard navigation support for country code dropdown
  const handleKeyDown = (e) => {
    console.log('handleKeyDown triggered:', e.key);
    console.log('type:', type);
    console.log('options:', options);

    if (type === 'dropdown' && options && options.length > 0 && options[0].displayValue) {
      console.log('Country code dropdown detected');
      const searchTerm = e.key.toLowerCase();
      console.log('Search term:', searchTerm);

      const matchingOption = options.find(
        (option) =>
          option.label.toLowerCase().includes(searchTerm) ||
          option.displayValue.toLowerCase().includes(searchTerm)
      );

      console.log('Matching option:', matchingOption);

      if (matchingOption) {
        // Find the select element and set its value
        const selectElement = e.target;
        console.log('Current select value:', selectElement.value);
        selectElement.value = matchingOption.value;
        console.log('New select value:', selectElement.value);

        // Trigger change event
        const changeEvent = new Event('change', { bubbles: true });
        selectElement.dispatchEvent(changeEvent);
        console.log('Change event dispatched');
      }
    }
  };

  // ============ HIDDEN ============
  if (type === 'hidden') {
    return (
      <input
        id={register_key}
        type="hidden"
        name={register_key}
        defaultValue={defaultValuePre}
        value={valuePre}
        required={isRequired}
        className={`w-full p-2 border-[1px] border-stone-300 rounded ${valuePre && 'bg-stone-200'}`}
        {...register(register_key)}
        onChange={onType ? (e) => onType(e.target.value) : () => { }}
        // ----------------
        // readOnly logic
        readOnly={readOnly}
        disabled={readOnly}
      // ----------------
      />
    );
  }

  // ============ PASSWORD ============
  if (type === 'password') {
    return (
      <div className="flex-shrink-0 flex flex-col gap-2 text-gray-700" style={style}>
        <div className="flex justify-between items-start space-x-2 w-fit">
          <span className="text-gray-600">{label}</span>
          {isRequired && <span className="text-red-500">*</span>}
        </div>
        <div className="relative">
          <input
            id={register_key}
            name={register_key}
            type={isEyeClick ? 'text' : 'password'}
            autoComplete={autoComplete || 'current-password'}
            defaultValue={defaultValuePre}
            value={valuePre}
            required={isRequired}
            className={`w-full p-2 pr-10 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ease-in-out ${readOnly ? 'bg-[#e7e5e4]' : ''
              }`}
            {...register(register_key)}
            onChange={onType ? (e) => onType(e.target.value) : () => { }}
            // ----------------
            // readOnly logic
            readOnly={readOnly}
            disabled={readOnly}
          // ----------------
          />
          {
            // Only show eye icon if not readOnly
            !readOnly && (
              <button
                type="button"
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                onClick={() => setIsEyeClick(!isEyeClick)}>
                {isEyeClick ? <RiEyeFill size={20} /> : <RiEyeCloseLine size={20} />}
              </button>
            )
          }
        </div>
      </div>
    );
  }

  // ============ DATE ============
  if (type === 'date') {
    return (
      <div className="flex-shrink-0 flex flex-col gap-2 text-gray-700" style={style}>
        <div className="flex justify-between items-start space-x-2 w-fit">
          <span className="text-gray-600">{label}</span>
          {isRequired && <span className="text-red-500">*</span>}
        </div>
        <input
          id={register_key}
          name={register_key}
          type="date"
          required={isRequired}
          defaultValue={defaultValuePre}
          value={valuePre}
          className={`w-full p-2 border-[1px] border-stone-300 rounded ${readOnly ? 'bg-[#e7e5e4]' : ''}`}
          {...register(register_key)}
          onChange={(e) => {
            if (onType) {
              onType(e.target.value);
            }
            register(register_key).onChange(e);
          }}
          max={CustomFeature?.max}
          min={CustomFeature?.min}
          // ----------------
          // readOnly logic
          readOnly={readOnly}
          disabled={readOnly}
        // ----------------
        />
      </div>
    );
  }

  if (type === 'date-year') {
    const currentYear = new Date().getFullYear();
    const startYear = CustomFeature?.minYear || 2000;
    const endYear = CustomFeature?.maxYear || currentYear;

    const years = [];
    for (let y = startYear; y <= endYear; y++) {
      years.push(y);
    }

    return (
      <div className="flex-shrink-0 flex flex-col gap-2 text-gray-700" style={style}>
        <div className="flex justify-between items-start space-x-2 w-fit">
          <span className="text-gray-600">{label}</span>
          {isRequired && <span className="text-red-500">*</span>}
        </div>
        <div className="relative w-full">
          <select
            id={register_key}
            name={register_key}
            required={isRequired}
            value={valuePre}
            className={`w-full p-2 pr-10 border-[1px] border-stone-300 rounded ${readOnly ? 'bg-[#e7e5e4]' : ''
              }`}
            {...register(register_key)}
            onChange={(e) => {
              if (onType) {
                onType(e.target.value);
              }
              register(register_key).onChange(e);
            }}
            disabled={readOnly}>
            <option value="" disabled>
              Select year
            </option>
            {years.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
          <img
            src={calendar}
            alt="calendar icon"
            className="pointer-events-none absolute right-6 top-1/2 -translate-y-1/2 w-4 h-4"
          />
        </div>
      </div>
    );
  }
  if (type === 'date-month') {
    const months = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December'
    ];

    // Normalize valuePre for selection
    const selectedValue = (() => {
      if (!valuePre) return '';
      if (typeof valuePre === 'number' || /^\d+$/.test(valuePre)) {
        const index = parseInt(valuePre, 10) - 1;
        return index >= 0 && index < 12 ? index + 1 : '';
      } else {
        const index = months.findIndex((m) => m.toLowerCase() === valuePre.toLowerCase());
        return index !== -1 ? index + 1 : '';
      }
    })();

    return (
      <div className="flex-shrink-0 flex flex-col gap-2 text-gray-700" style={style}>
        <div className="flex justify-between items-start space-x-2 w-fit">
          <span className="text-gray-600">{label}</span>
          {isRequired && <span className="text-red-500">*</span>}
        </div>
        <div className="relative w-full">
          <select
            id={register_key}
            name={register_key}
            required={isRequired}
            value={selectedValue}
            className={`w-full p-2 pr-10 border-[1px] border-stone-300 rounded ${readOnly ? 'bg-[#e7e5e4]' : ''
              }`}
            {...register(register_key)}
            onChange={(e) => {
              const val = parseInt(e.target.value, 10);
              const monthName = months[val - 1];
              if (onType) {
                onType({ id: val, name: monthName }); // send both name & id
              }
              register(register_key).onChange(e);
            }}
            disabled={readOnly}>
            <option value="" disabled>
              Select month
            </option>
            {months.map((month, index) => (
              <option key={month} value={index + 1}>
                {month}
              </option>
            ))}
          </select>
          <img
            src={calendar}
            alt="calendar icon"
            className="pointer-events-none absolute right-6 top-1/2 -translate-y-1/2 w-4 h-4"
          />
        </div>
      </div>
    );
  }


  /// ============== date-month-superadmin ============== 
  if (type === 'date-month-superadmin') {
    const months = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December'
    ];

    // Normalize valuePre for selection
    const selectedValue = (() => {
      if (!valuePre) return '';
      if (typeof valuePre === 'number' || /^\d+$/.test(valuePre)) {
        const index = parseInt(valuePre, 10) - 1;
        return index >= 0 && index < 12 ? index + 1 : '';
      } else {
        const index = months.findIndex((m) => m.toLowerCase() === valuePre.toLowerCase());
        return index !== -1 ? index + 1 : '';
      }
    })();

    return (
      <div className="flex-shrink-0 flex flex-col  text-gray-700" style={style}>
        <div className="flex justify-between items-start space-x-2 w-fit">
          <span className="text-gray-600">{label}</span>
          {isRequired && <span className="text-red-500">*</span>}
        </div>
        <div className="relative w-full">
          <select
            id={register_key}
            name={register_key}
            required={isRequired}
            value={selectedValue}
            className={`w-full p-2 pr-10 border-[1px] border-stone-300 rounded ${readOnly ? 'bg-[#e7e5e4]' : ''
              }`}
            {...register(register_key)}
            onChange={(e) => {
              const val = parseInt(e.target.value, 10);
              const monthName = months[val - 1];
              if (onType) {
                onType({ id: val, name: monthName }); // send both name & id
              }
              register(register_key).onChange(e);
            }}
            disabled={readOnly}>
            <option value="" disabled>
              Select month
            </option>
            {months.map((month, index) => (
              <option key={month} value={index + 1}>
                {month}
              </option>
            ))}
          </select>
          <img
            src={calendar}
            alt="calendar icon"
            className="pointer-events-none absolute right-6 top-1/2 -translate-y-1/2 w-4 h-4"
          />
        </div>
      </div>
    );
  }

  // ============ CUSTOM BuildingPermissonDate ============
  if (type === 'BuildingPermissonDate') {
    return (
      <div className="flex-shrink-0 flex flex-col gap-2 text-gray-700" style={style}>
        <div className="flex justify-between items-start space-x-2 w-fit">
          <span className="text-gray-600">{label}</span>
          {isRequired && <span className="text-red-500">*</span>}
        </div>
        <input
          id={register_key}
          name={register_key}
          type="date"
          required={isRequired}
          defaultValue={defaultValuePre}
          value={valuePre}
          className={`w-full p-2 border-[1px] border-stone-300 rounded ${valuePre && 'bg-stone-200'
            } ${readOnly ? 'bg-[#e7e5e4]' : ''}`}
          {...register(register_key)}
          onChange={(e) => {
            if (onType) {
              onType(e.target.value);
            }
            register(register_key).onChange(e);
          }}
          max={CustomFeature?.max}
          min={CustomFeature?.min}
          // ----------------
          // readOnly logic
          readOnly={readOnly}
          disabled={readOnly}
        // ----------------
        />
      </div>
    );
  }

  // ============ CHECKBOX ============
  if (type === 'checkbox') {
    return (
      <div className="flex-shrink-0 flex flex-col gap-2 text-gray-700" style={style}>
        <div className="flex justify-between items-start space-x-2 w-fit">
          <span className="text-gray-600">{label}</span>
          {isRequired && <span className="text-red-500">*</span>}
        </div>
        <input
          id={register_key}
          name={register_key}
          type="checkbox"
          required={isRequired}
          defaultValue={defaultValuePre}
          value={valuePre}
          className={`w-full p-2 border-[1px] border-stone-300 rounded ${valuePre && 'bg-stone-200'
            } ${readOnly ? 'bg-[#e7e5e4]' : ''}`}
          {...register(register_key)}
          onChange={onType ? (e) => onType(e.target.value) : () => { }}
          // ----------------
          // readOnly logic
          disabled={readOnly}
        // ----------------
        />
      </div>
    );
  }

  // ============ FILE (Basic) ============
  if (type === 'file') {
    return (
      <div className="flex-shrink-0 flex flex-col gap-2 text-gray-700" style={style}>
        <div className="flex justify-between items-start space-x-2 w-fit">
          <span className="text-gray-600">{label}</span>
          {isRequired && <span className="text-red-500">*</span>}
        </div>
        <div className="flex justify-between w-full space-x-4 items-stretch">
          <input
            type="text"
            className="w-[60%] border-2 border-stone-200 rounded-md"
            // readOnly background
            readOnly
            style={readOnly ? { backgroundColor: '#e7e5e4' } : {}}
          />
          <label
            htmlFor={register_key}
            className={`relative flex justify-center items-center gap-2 w-[40%] ${readOnly ? 'bg-stone-200 text-gray-400' : 'bg-[#346C75] text-gray-100'
              } font-semibold rounded p-2 cursor-pointer`}>
            <input
              name={register_key}
              type="file"
              id={register_key}
              required={isRequired}
              className="absolute opacity-0 w-full h-full cursor-pointer"
              {...register(register_key)}
              onChange={handleFileChange}
              accept={accept}
              // readOnly logic
              disabled={readOnly}
            />
            {readOnly ? 'File Upload Disabled' : 'Choose File'}
          </label>
        </div>
        {renderSelectedFiles()}
      </div>
    );
  }

  // ============ FILE-BTN ============
  if (type === 'file-btn') {
    return (
      <div className="flex flex-col gap-2 text-gray-700" style={style}>
        <div className="flex items-start gap-1 w-fit">
          <span className="text-gray-600">{label}</span>
          {isRequired && <span className="text-red-500">*</span>}
        </div>
        <div className="flex flex-col items-start space-y-2">
          <label
            htmlFor={register_key}
            className={`relative inline-flex justify-center items-center min-w-[9.5rem] px-4 py-2 cursor-pointer border border-[#346C75] text-sm font-semibold rounded text-[#346C75] hover:bg-[#346C75]/5 ${
              readOnly ? 'bg-stone-200 text-stone-500 cursor-not-allowed' : ''
            }`}>
            <input
              name={register_key}
              type="file"
              id={register_key}
              required={isRequired}
              className="absolute inset-0 opacity-0 cursor-pointer disabled:cursor-not-allowed"
              onChange={handleFileChange}
              accept={accept}
              disabled={readOnly}
            />
            {readOnly ? 'File Upload Disabled' : 'Choose File'}
          </label>
          {renderSelectedFiles()}
        </div>
      </div>
    );
  }

  //create a type file-icon-btn which should be just an upload icon and rest should be hidden and on click of that icon file should be uploaded
  // ============ FILE-ICON-BTN ============

  // ============ FILE-ICON-BTN ============
  if (type === 'file-icon-btn') {
    return (
      <div className="flex-shrink-0 flex flex-col gap-2 text-gray-700" style={style}>
        {/* <div className="flex justify-between items-start space-x-2 w-fit">
          <span className="text-gray-600">{label}</span>
          {isRequired && <span className="text-red-500">*</span>}
        </div> */}
        <div className="flex flex-col w-full space-y-2">
          {/* The upload icon button */}
          <Button
            displayType={'downloadButton'}
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            customStyle={{
              padding: '0.5rem 1rem',
              fontSize: '13px',
              display: 'flex',
              gap: '5px',
              alignItems: 'center'
            }}
            disabled={readOnly}>
            Sign
            <FiUpload size={17} />
          </Button>
          {/* The hidden file input */}
          <input
            name={register_key}
            type="file"
            id={register_key}
            ref={fileInputRef}
            required={isRequired}
            className="hidden"
            onChange={handleFileChange}
            accept={accept}
            disabled={readOnly}
          />
          {renderSelectedFiles()}
        </div>
      </div>
    );
  }

  // ============ FILE-SECONDARY (multiple) ============
  if (type === 'file-secondary') {
    return (
      <div className="flex-shrink-0 flex flex-col gap-2 text-gray-700" style={style}>
        <div className="flex justify-between items-start space-x-2 w-fit">
          <span className="text-gray-600">{label}</span>
          {isRequired && <span className="text-red-500">*</span>}
        </div>
        <div className="flex flex-col w-full space-y-2">
          <label
            htmlFor={register_key}
            className={`flex-grow relative flex justify-center items-center gap-2 border-[1px] border-[#346C75] text-[#346C75] font-semibold rounded p-2 cursor-pointer ${readOnly ? 'bg-stone-200 text-stone-500' : ''
              }`}>
            <input
              name={register_key}
              type="file"
              id={register_key}
              required={isRequired}
              multiple
              accept={accept}
              className="absolute opacity-0 w-full h-full cursor-pointer"
              onChange={handleFileChange}
              {...register(register_key, {
                onChange: (e) => handleFileChange(e)
              })}
              disabled={readOnly} // readOnly logic
            />
            {readOnly ? 'File Upload Disabled' : 'Choose Files'}
          </label>
          {renderSelectedFiles()}
        </div>
      </div>
    );
  }

  // ============ DROPDOWN (single) ============
  if (type === 'CountyCodedropdown') {
    console.log('CountyCodedropdown - valuePre:', valuePre);
    console.log('CountyCodedropdown - options:', options);

    // Filter options based on search term
    const filteredOptions =
      options?.filter(
        (option) =>
          option.label?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          option.displayValue?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          option.value?.toLowerCase().includes(searchTerm.toLowerCase())
      ) || [];

    // Get selected option for display
    const selectedOption = options?.find((opt) => String(opt.value) === String(valuePre));
    console.log('CountyCodedropdown - selectedOption:', selectedOption);

    return (
      <div
        className="flex-shrink-0 flex flex-col gap-2 text-gray-600"
        style={style}
        ref={dropdownRef}>
        <div className="flex justify-between items-start space-x-2 w-fit">
          <span className="text-gray-600">{label}</span>
          {isRequired && <span className="text-red-500">*</span>}
        </div>

        <div className="relative">
          {/* Hidden select for form validation */}
          <select
            name={register_key}
            required={isRequired}
            className="hidden"
            {...register(register_key)}
            value={valuePre || ''}
            disabled={readOnly}>
            {options?.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          {/* Custom dropdown UI */}
          <div
            className={`w-full p-2 border-[1px] border-stone-300 rounded cursor-pointer ${readOnly ? 'bg-[#e7e5e4]' : 'bg-white'
              } ${isDropdownOpen ? 'border-blue-500' : ''}`}
            onClick={() => !readOnly && setIsDropdownOpen(!isDropdownOpen)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                if (!readOnly) {
                  setIsDropdownOpen(!isDropdownOpen);
                }
              }
            }}
            tabIndex={0}>
            <div className="flex justify-between items-center">
              <span className={selectedOption ? 'text-gray-900' : 'text-gray-500'}>
                {selectedOption ? selectedOption.label : 'Select country'}
              </span>
              <svg
                className={`w-4 h-4 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>
          </div>

          {/* Dropdown options */}
          {isDropdownOpen && !readOnly && (
            <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-hidden">
              {/* Search input */}
              <div className="p-2 border-b border-gray-200">
                <input
                  type="text"
                  placeholder="Search countries..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500"
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      setIsDropdownOpen(false);
                      setSearchTerm('');
                    }
                  }}
                  autoFocus
                />
              </div>

              {/* Options list */}
              <div className="max-h-48 overflow-y-auto">
                {filteredOptions.length > 0 ? (
                  filteredOptions.map((option) => (
                    <div
                      key={option.value}
                      className={`p-2 cursor-pointer hover:bg-gray-100 ${String(option.value) === String(valuePre) ? 'bg-blue-50 text-blue-600' : ''
                        }`}
                      onClick={() => {
                        console.log('Option clicked:', option);
                        const event = { target: { value: option.value } };
                        console.log('Event created:', event);
                        handleChangedropdown(event);
                        // Also call the onDropdownChange prop if provided
                        if (onDropdownChange) {
                          console.log('Calling onDropdownChange with:', option.value);
                          onDropdownChange(option.value);
                        }
                        setIsDropdownOpen(false);
                        setSearchTerm('');
                      }}>
                      {option.label}
                    </div>
                  ))
                ) : (
                  <div className="p-2 text-gray-500 text-sm">No countries found</div>
                )}
              </div>
            </div>
          )}
        </div>

        {errors?.message && <p className="text-red-500">{errors.message}</p>}
      </div>
    );
  }

  if (type === 'dropdown') {
    return (
      <div className="flex-shrink-0 flex flex-col gap-2 text-gray-600" style={style}>
        <div className="flex justify-between items-start space-x-2 w-fit">
          <span className="text-gray-600">{label}</span>
          {isRequired && !hideAstrik && <span className="text-red-500">*</span>}
        </div>
        <select
          name={register_key}
          required={isRequired}
          className={`w-full p-2 border-[1px] border-stone-300 rounded ${readOnly ? 'bg-[#e7e5e4]' : ''
            }`}
          {...register(register_key)}
          onChange={handleChangedropdown}
          disabled={readOnly} // readOnly logic
        >
          {valuePre && <option value={valuePre}>{valuePre}</option>}
          {defaultValuePre && <option defaultValue={defaultValuePre}>{defaultValuePre}</option>}
          {options?.map((option) => (
            <option
              key={option.value}
              value={option.value}
              disabled={option.disabled}
              selected={option.selected}>
              {option.label}
            </option>
          ))}
          {defaultbtn && <option value="action-option">{defaultbtn}</option>}
        </select>
        {errors?.message && <p className="text-red-500">{errors.message}</p>}
      </div>
    );
  }

  // ============ DROPDOWN (with auto filter) ============
  if (type === 'dropdown-autofilter') {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');

    const filteredOptions = (options || []).filter((opt) =>
      opt.label.toLowerCase().includes(search.toLowerCase())
    );

    const selectedOption = options?.find(
      (opt) => String(opt.value) === String(valuePre)
    );

    return (
      <div className="flex-shrink-0 flex flex-col gap-2 text-gray-600" style={style}>
        <div className="flex justify-between items-start space-x-2 w-fit">
          <span className="text-gray-600">{label}</span>
          {isRequired && !hideAstrik && <span className="text-red-500">*</span>}
        </div>

        {/* Hidden select for react-hook-form */}
        <select
          name={register_key}
          className="hidden"
          value={valuePre || ''}
          {...register(register_key)}
          onChange={() => { }}
        >
          {options?.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>

        {/* Custom dropdown UI */}
        <div className="relative" style={{ overflow: "visible" }}>
          <div
            className={`w-full p-2 border border-stone-300 rounded cursor-pointer flex justify-between items-center ${readOnly ? 'bg-[#e7e5e4]' : 'bg-white'}`}
            onClick={() => !readOnly && setIsOpen(!isOpen)}
          >
            <span>{selectedOption ? selectedOption.label : 'Select'}</span>

            {/* Dropdown Arrow */}
            <svg
              className={`w-4 h-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>


          {isOpen && !readOnly && (
            <div className="absolute z-[999999] w-full bg-white border border-gray-300 rounded shadow-md mt-1 max-h-60 overflow-auto">

              {/* Search Box */}
              <input
                type="text"
                placeholder="Search…"
                value={search}
                onChange={(e) => {
                  const next = e.target.value;
                  setSearch(next);
                  onSearchChange && onSearchChange(next);
                }}
                className="w-full p-2 border-b border-gray-200 text-sm outline-none"
                autoFocus
              />

              {/* Options list */}
              {filteredOptions.map((opt) => (
                <div
                  key={opt.value}
                  className={`p-2 cursor-pointer hover:bg-gray-100 ${String(opt.value) === String(valuePre)
                    ? 'bg-blue-50 text-blue-600'
                    : ''
                    }`}
                  onClick={() => {
                    const event = { target: { value: opt.value } };

                    // Notify parent form
                    onDropdownChange && onDropdownChange(opt.value);

                    // Update react-hook-form
                    register(register_key).onChange(event);

                    setIsOpen(false);
                    setSearch('');
                  }}
                >
                  {opt.label}
                </div>
              ))}

              {filteredOptions.length === 0 && (
                <div className="p-2 text-gray-400 text-sm">No results found</div>
              )}
            </div>
          )}
        </div>

        {errors?.message && <p className="text-red-500">{errors.message}</p>}
      </div>
    );
  }

  // ============ DROPDOWN-MULTIPLE ============
  if (type === 'dropdown-multiple') {
    return (
      <div className="flex-shrink-0 flex flex-col gap-2 text-gray-600" style={style}>
        <div className="flex justify-between items-start space-x-2 w-fit">
          <span className="text-gray-600">{label}</span>
          {isRequired && <span className="text-red-500">*</span>}
        </div>

        {/* If readOnly => disable the Multiselect */}
        {readOnly ? (
          <div className={`p-2 border border-stone-300 rounded bg-[#e7e5e4]`}>
            {(selectedValues || []).map((item, index) => (
              <span key={index} className="m-1 p-1 bg-gray-400 text-white rounded text-sm">
                {item.name}
              </span>
            ))}
          </div>
        ) : (
          <Multiselect
            options={options} // Options to display in the dropdown
            selectedValues={selectedValues} // Preselected value
            onSelect={(selectedList) => {
              setSelectedValues(selectedList);
              register(register_key, {
                value: selectedList
              });
            }}
            onRemove={(selectedList) => {
              setSelectedValues(selectedList);
              register(register_key, {
                value: selectedList
              });
            }}
            displayValue="name"
            required={isRequired}
            style={{
              optionContainer: {
                border: '1px solid',
                backgroundColor: 'white'
              },
              option: {
                color: 'black',
                backgroundColor: 'white'
              },
              chips: {
                background: '#2A565E',
                color: 'white'
              }
            }}
          />
        )}
      </div>
    );
  }

  // ============ TEXT ON TYPE ============
  if (type === 'textOnType') {
    return (
      <div className="flex-shrink-0 flex flex-col gap-2 text-gray-700" style={style}>
        <div className="flex justify-between items-start space-x-2 w-fit">
          <span className="text-gray-600">{label}</span>
          {isRequired && <span className="text-red-500">*</span>}
        </div>
        <input
          name={register_key}
          type="text"
          defaultValue={defaultValuePre}
          value={valuePre}
          required={isRequired}
          className={`w-full p-2 border-[1px] border-stone-300 rounded ${valuePre && 'bg-stone-200'
            } ${readOnly ? 'bg-[#e7e5e4]' : ''}`}
          {...register(register_key, {
            required: isRequired ? 'This field is required' : false,
            ...(register_key === 'google_map_link' && {
              pattern: {
                value: /^(https?:\/\/)?(www\.)?google\.com\/maps\/.+$/i,
                message: 'Please enter a valid Google Map link'
              }
            })
          })}
          onChange={onType ? (e) => onType(e) : () => { }}
          maxLength={maxLength}
          style={CustomStyle}
          minLength={minLength}
          placeholder={placeholder}
          // -----------
          // readOnly
          readOnly={readOnly}
          disabled={readOnly}
        // -----------
        />
        {errors && <p className="text-red-500">{errors.message}</p>}
      </div>
    );
  }

  // ============ NUMBER ============
  if (type === 'number') {
    return (
      <div className="flex-shrink-0 flex flex-col gap-2 text-gray-700" style={style}>
        <div className="flex justify-between items-start space-x-2 w-fit">
          <span className="text-gray-600">{label}</span>
          {isRequired && <span className="text-red-500">*</span>}
        </div>
        <input
          id={register_key}
          name={register_key}
          type="text"
          defaultValue={defaultValuePre}
          value={valuePre}
          required={isRequired}
          className={`w-full p-2 border-[1px] border-stone-300 rounded ${readOnly ? 'bg-[#e7e5e4]' : ''
            }`}
          {...register(register_key)}
          onChange={(e) => {
            const value = e.target.value;
            // Only allow numbers and decimals
            if (value === '' || /^\d*\.?\d*$/.test(value)) {
              if (onType) {
                onType(value);
              }
            }
          }}
          onInput={(e) => {
            let value = e.target.value;
            // Remove all non-numeric characters except dots
            value = value.replace(/[^0-9.]/g, '');
            // Ensure only one decimal point
            const parts = value.split('.');
            if (parts.length > 2) {
              value = parts[0] + '.' + parts.slice(1).join('');
            }
            e.target.value = value;
          }}
          maxLength={maxLength}
          minLength={minLength}
          placeholder={placeholder}
          // ---------
          // readOnly
          readOnly={readOnly}
          disabled={readOnly}
        // ---------
        />
        {errors?.message && <p className="text-red-500">{errors.message}</p>}
      </div>
    );
  }

  // ============ TEXTAREA ============
  if (type === 'textarea') {
    return (
      <div className="flex-shrink-0 flex flex-col gap-2 text-gray-700" style={style}>
        <div className="flex justify-between items-start space-x-2 w-fit">
          <span className="text-gray-600">{label}</span>
          {isRequired && <span className="text-red-500">*</span>}
        </div>
        <textarea
          id={register_key}
          name={register_key}
          defaultValue={defaultValuePre}
          value={valuePre}
          required={isRequired}
          className={`w-full p-2 border-[1px] border-stone-300 rounded ${readOnly ? 'bg-[#e7e5e4]' : ''
            }`}
          {...register(register_key)}
          onChange={onType ? (e) => onType(e.target.value) : () => { }}
          maxLength={maxLength}
          minLength={minLength}
          placeholder={placeholder}
          // ---------
          // readOnly
          readOnly={readOnly}
          disabled={readOnly}
        // ---------
        />
        {errors?.message && <p className="text-red-500">{errors.message}</p>}
      </div>
    );
  }

  if (type === 'idleTimeInput') {
    return (
      <div className="flex-shrink-0 flex flex-col gap-2 text-gray-700" style={style}>
        <div className="flex-col justify-start items-start">
          <div className="flex justify-between items-start space-x-2 w-fit">
            <span className="text-gray-600">
              {label}
              Idle Time{' '}
            </span>
            {isRequired && !hideAstrik && <span className="text-red-500">*</span>}
          </div>
          <span className="text-orange-400 text-xs">(days to wait after dependant tasks)</span>
        </div>
        <input
          id={register_key}
          name={register_key}
          type="text"
          defaultValue={defaultValuePre}
          value={valuePre}
          autoComplete={autoComplete}
          required={isRequired}
          className={`w-full p-2 border-[1px] border-stone-300 rounded ${readOnly ? 'bg-[#e7e5e4]' : ''
            }`}
          {...register(register_key)}
          onChange={onType ? (e) => onType(e.target.value) : () => { }}
          maxLength={maxLength}
          style={CustomStyle}
          minLength={minLength}
          placeholder={placeholder}
          // ----------
          // readOnly
          readOnly={readOnly}
          disabled={readOnly}
        // ----------
        />
        {errors?.message && <p className="text-red-500">{errors.message}</p>}
      </div>
    );
  }

  // ============ DEFAULT (type="text") ============
  return (
    <div className="flex-shrink-0 flex flex-col gap-2 text-gray-700" style={style}>
      <div className="flex justify-between items-start space-x-2 w-fit">
        <span className="text-gray-600">{label}</span>
        {isRequired && !hideAstrik && <span className="text-red-500">*</span>}
      </div>
      <input
        id={register_key}
        name={register_key}
        type="text"
        defaultValue={defaultValuePre}
        value={valuePre}
        autoComplete={autoComplete}
        required={isRequired}
        className={`w-full p-2 border-[1px] border-stone-300 rounded ${readOnly ? 'bg-[#e7e5e4]' : ''
          }`}
        {...register(register_key)}
        onChange={onType ? (e) => onType(e.target.value) : () => { }}
        maxLength={maxLength}
        style={CustomStyle}
        minLength={minLength}
        placeholder={placeholder}
        // ----------
        // readOnly
        readOnly={readOnly}
        disabled={readOnly}
      // ----------
      />
      {errors?.message && <p className="text-red-500">{errors.message}</p>}
    </div>
  );
};

export default FormInput;
